// ===========================================================================
// Module A — Post-Discharge Care Coordination · Service layer
//
// Hoy lee los dumps JSON diarios de src/pages/Main/Temp/DataDashCareManager.
// Los campos de workflow (follow-up status, intentos de outreach, ventana TCM)
// NO existen aún en el dump: se derivan DETERMINÍSTICAMENTE desde cipId para
// que la UI sea estable entre renders. SIMULADO hasta que exista el endpoint
// backend (census.TAB_TRANSITION_FOLLOWUP / TAB_TRANSITION_OUTREACH_LOG).
// ===========================================================================

import { FOLLOW_UP_STATUS, TCM_WINDOW } from "../common/constants";

/** Selección determinística desde cipId (estable entre renders). */
const pick = (cipId, arr) => arr[Number(cipId) % arr.length];

/**
 * Deriva el follow-up status de forma determinística (cipId % 10),
 * acotado por los días desde el alta (un alta de ayer no puede estar COMPLETED).
 * SIMULADO hasta que el backend exponga census.TAB_TRANSITION_FOLLOWUP.
 */
function deriveFollowUpStatus(cipId, daysSinceDischarge) {
  const bucket = Number(cipId) % 10;
  let status;
  if (bucket <= 2) status = FOLLOW_UP_STATUS.PENDING;
  else if (bucket <= 4) status = FOLLOW_UP_STATUS.OUTREACH;
  else if (bucket <= 6) status = FOLLOW_UP_STATUS.SCHEDULED;
  else if (bucket <= 8) status = FOLLOW_UP_STATUS.COMPLETED;
  else status = FOLLOW_UP_STATUS.NO_SHOW;

  // Coherencia temporal: visitas completadas/no-show requieren días transcurridos.
  if (daysSinceDischarge <= 2 &&
      (status === FOLLOW_UP_STATUS.COMPLETED || status === FOLLOW_UP_STATUS.NO_SHOW)) {
    return FOLLOW_UP_STATUS.OUTREACH;
  }
  return status;
}

/** Ventana TCM según días desde el alta (CPT 99496 ≤7d, 99495 ≤14d). */
function deriveTcmWindow(daysSinceDischarge) {
  if (daysSinceDischarge == null) return TCM_WINDOW.EXPIRED;
  if (daysSinceDischarge <= 7) return TCM_WINDOW.WINDOW_7D;
  if (daysSinceDischarge <= 14) return TCM_WINDOW.WINDOW_14D;
  return TCM_WINDOW.EXPIRED;
}

/** Prioridad clínica derivada del LACE score (≥13 alto, 10-12 medio). */
function derivePriority(laceScore) {
  if ((laceScore ?? 0) >= 13) return "HIGH";
  if ((laceScore ?? 0) >= 10) return "MEDIUM";
  return "LOW";
}

/** Fecha objetivo de visita = alta + 7 días (regla del módulo). */
function deriveTargetVisitDate(dischargeDate) {
  if (!dischargeDate) return null;
  const d = new Date(`${dischargeDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}

/**
 * Obtiene la cola de trabajo de altas recientes (work queue) de una location,
 * enriquecida con campos de workflow post-alta.
 *
 * FUTURO ENDPOINT BACKEND: GET /api/Transitions/followups?locationKey={key}
 * (respaldado por census.TAB_TRANSITION_FOLLOWUP). Hoy: dump JSON local +
 * derivación determinística simulada.
 *
 * @param {string} locationKey - Key de la location (e.g. "fort_myers")
 *
 * Ejemplo de respuesta:
 * {
 *   "stats": { "totalDischarges": 10, "avgLaceScore": 13.2, "highLaceCount": 6, ... },
 *   "discharges": [
 *     {
 *       "cipId": 42500094114, "name": "SHERLOCK, KYLE",
 *       "facility": "FLHIE - LEE MEMORIAL HOSPITAL", "laceScore": 18, "los": 4,
 *       "daysSinceDischarge": 1, "pcpName": "SIDDIKI, AWAIS MD",
 *       // —— derivados (simulados) ——
 *       "followUpStatus": "OUTREACH", "priority": "HIGH",
 *       "tcmWindow": "WINDOW_7D", "tcmDaysRemaining": 13,
 *       "outreachAttempts": 1, "targetVisitDate": "2026-06-18"
 *     }
 *   ]
 * }
 *
 * @returns {Promise<{stats: Object, discharges: Object[]}>}
 */
export async function getDischargeWorkQueue(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashCareManager/${locationKey}/transitions.json`
  );
  const data = module.default || module;
  const discharges = (data.discharges || []).map((d) => {
    const followUpStatus = deriveFollowUpStatus(d.cipId, d.daysSinceDischarge ?? 0);
    return {
      ...d,
      followUpStatus,
      priority: derivePriority(d.laceScore),
      tcmWindow: deriveTcmWindow(d.daysSinceDischarge),
      tcmDaysRemaining: Math.max(0, 14 - (d.daysSinceDischarge ?? 99)),
      // Intentos previos de outreach — SIMULADO (cipId % 3)
      outreachAttempts:
        followUpStatus === FOLLOW_UP_STATUS.PENDING ? 0 : 1 + (Number(d.cipId) % 3),
      targetVisitDate: deriveTargetVisitDate(d.dischargeDate),
      // Disposición simulada cuando el dump no la trae
      disposition:
        d.disposition || pick(d.cipId, ["Home", "Home with HH", "SNF", "Home", "Rehab"]),
    };
  });

  // Cola priorizada: LACE desc, luego días desde alta asc (ventana más urgente)
  discharges.sort(
    (a, b) =>
      (b.laceScore ?? 0) - (a.laceScore ?? 0) ||
      (a.daysSinceDischarge ?? 0) - (b.daysSinceDischarge ?? 0)
  );

  return { stats: data.stats || {}, discharges };
}
